
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-foreground hover:bg-primary/85 active:bg-primary/75 data-[state=open]:bg-primary/80 aria-pressed:bg-primary/80 focus-visible:ring-primary",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:bg-muted/30 hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/85",
        ghost: "hover:bg-muted/20 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  debugLabel?: string
  preventDoubleClick?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onClick, disabled, debugLabel, preventDoubleClick = true, children, ...props }, ref) => {
    const [isProcessing, setIsProcessing] = React.useState(false);
    const Comp = asChild ? Slot : "button"
    
    const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent double clicks if enabled
      if (preventDoubleClick && isProcessing) {
        if (import.meta.env.MODE === "development") {
          console.log("Button click prevented: Double click protection", { debugLabel });
        }
        return;
      }

      // Development logging
      if (import.meta.env.MODE === "development") {
        console.log("Button triggered:", debugLabel || "Unnamed Button", {
          variant,
          disabled,
          timestamp: new Date().toISOString()
        });
      }

      // Guard against clicks on disabled buttons
      if (disabled) {
        if (import.meta.env.MODE === "development") {
          console.log("Button click blocked: Button is disabled", { debugLabel });
        }
        return;
      }

      if (typeof onClick === 'function') {
        try {
          setIsProcessing(true);
          
          // Simply call the onClick handler - React onClick handlers should return void
          onClick(event);
          
        } catch (error) {
          if (import.meta.env.MODE === "development") {
            console.error("Button click error:", error, { debugLabel });
          }
        } finally {
          setIsProcessing(false);
        }
      }
    };

    const isDisabled = disabled || isProcessing;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...(asChild ? {} : { onClick: handleClick, disabled: isDisabled })}
        {...props}
      >
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
